import { ExternalLink, FolderGit2, ScrollText } from 'lucide-react';
import { Fragment, useState } from 'react';
import { RepoChip } from '@/components/common/bits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Panel, PanelHead } from '@/components/ui/panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import { AddRepoButton, AddRepoComposer } from '@/features/admin/AddRepoComposer';
import { AdminShell } from '@/features/admin/AdminShell';
import { FleetPromptEditor, RepoPromptEditor } from '@/features/admin/PromptEditor';
import { useFleetPrompt, useRepos } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { api, type RepoRow } from '@/lib/api';
import { ago, minutesSince } from '@/lib/format';

/**
 * The repo registry (issue #101): every slug an admin has said the job
 * composer may offer, and the door for adding one.
 *
 * Before this existed, a repo became real in Dyspatch the moment somebody
 * typed its name into a job - free text, no check - and a typo produced a job
 * no worker would ever pick up. This page, and the picker on the job composer
 * that reads the same list, are the two ends of closing that gap: this is
 * where a repo is confirmed to exist and admitted; the composer is where a
 * typo is now structurally impossible because there is no longer a text field
 * to typo into.
 *
 * Disabling is never a delete. `packages/db/src/schema/repos.ts` explains why
 * at the column: the row is the only record of who added a repo and when, and
 * a job that already named it keeps naming it whether or not the picker still
 * offers it going forward.
 */

/**
 * What the repo says about its own pull requests, in the registry row.
 *
 * `.dyspatch.json` declares the gates a branch has to pass before a person is
 * asked to look at it, and a repo with no declaration opens pull requests with
 * nothing verified. The only place that has ever been said is `doctor`, on an
 * engineer's laptop, so the admin who registered the repo learned it from a PR
 * that had been through no checks at all.
 *
 * Four states, not three. "The hub could not look" is not "there is no file",
 * and a row that conflates them tells an admin their repo has no gates on a
 * hub that simply has no GitHub token.
 */
function GatesCell({ repo }: { repo: RepoRow }) {
  const [open, setOpen] = useState(false);
  const recheck = useHubAction(
    async () => {
      await api.recheckRepo(repo.slug);
    },
    { success: `Re-read ${repo.slug}'s .dyspatch.json.` },
  );

  if (repo.dyspatchState === null) {
    return <span className="text-fg-3 text-xs">not read</span>;
  }
  if (repo.dyspatchState === 'unreadable') {
    return <span className="text-warn text-xs">.dyspatch.json could not be used</span>;
  }
  if (repo.dyspatchState === 'absent' || (repo.dyspatchGates ?? []).length === 0) {
    return (
      <div className="min-w-0 space-y-0.5">
        <span className="text-warn block text-xs">
          {repo.dyspatchState === 'absent' ? 'no .dyspatch.json' : 'no gates declared'}
        </span>
        <div className="flex flex-wrap items-center gap-x-2">
          <button
            type="button"
            className="text-fg-3 hover:text-foreground text-2xs underline underline-offset-2"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Hide the shape' : 'What one looks like'}
          </button>
          <button
            type="button"
            className="text-fg-3 hover:text-foreground text-2xs underline underline-offset-2"
            disabled={recheck.busy}
            onClick={() => recheck.run()}
          >
            {recheck.busy ? 'Reading…' : 'Check again'}
          </button>
        </div>
        {open ? (
          <pre className="bg-sunken dy-scroll mt-1 overflow-x-auto rounded-md border px-2 py-1.5 font-mono text-2xs select-all">
            {DISPATCH_FILE_SHAPE}
          </pre>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-0.5">
      <span className="text-fg-2 block truncate font-mono text-2xs">
        {(repo.dyspatchGates ?? []).join(', ')}
      </span>
      <button
        type="button"
        className="text-fg-3 hover:text-foreground text-2xs underline underline-offset-2"
        disabled={recheck.busy}
        onClick={() => recheck.run()}
      >
        {recheck.busy ? 'Reading…' : 'Check again'}
      </button>
    </div>
  );
}

/** The file, as short as it can be and still say something. One click away from a repo that has none. */
const DISPATCH_FILE_SHAPE = `{
  "gates": ["bun run typecheck", "bun test"],
  "setup": "bun install",
  "mcpServers": []
}`;

function RepoActions({
  repo,
  onDisable,
  onEditPrompt,
  editing,
}: {
  repo: RepoRow;
  onDisable: () => void;
  onEditPrompt: () => void;
  editing: boolean;
}) {
  const enable = useHubAction(
    async () => {
      await api.setRepoEnabled(repo.slug, true);
    },
    { success: `${repo.slug} is back in the job composer's picker.` },
  );

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button variant="ghost" size="xs" onClick={onEditPrompt}>
        <ScrollText aria-hidden />
        {editing ? 'Close' : 'Instructions'}
      </Button>
      {repo.enabled ? (
        <Button variant="outline" size="xs" onClick={onDisable}>
          Disable
        </Button>
      ) : (
        <Button variant="outline" size="xs" onClick={() => enable.run()} disabled={enable.busy}>
          {enable.busy ? 'Enabling…' : 'Enable'}
        </Button>
      )}
    </div>
  );
}

export function AdminReposPage() {
  const repos = useRepos();
  const fleetPrompt = useFleetPrompt();
  const rows = repos.data ?? [];
  const [adding, setAdding] = useState(false);
  /** The one repo whose instructions are open, by slug. One at a time: two 20k textareas side by side is not an edit, it is a diff nobody asked for. */
  const [editing, setEditing] = useState<string | null>(null);

  const ask = useAsk();
  const disable = useHubAction(
    async (slug: string) => {
      await api.setRepoEnabled(slug, false);
    },
    {
      success: (slug) => `${slug} disabled. Jobs that already name it still run.`,
      onDone: () => ask.close(),
    },
  );

  return (
    <AdminShell tab="repos" actions={<AddRepoButton onClick={() => setAdding(true)} />}>
      <AddRepoComposer open={adding} onClose={() => setAdding(false)} />

      {/*
        The fleet level comes first because it is what every row below inherits.
        An admin who reads the project editor without having seen this one would
        have no way to know their text is the second half of something.
      */}
      <Panel>
        <PanelHead title="Fleet instructions" meta={fleetPrompt.data ? 'set' : 'not set'} />
        {fleetPrompt.isPending ? (
          <p className="text-fg-3 px-gutter py-8 text-center text-sm">Loading…</p>
        ) : (
          <FleetPromptEditor
            key={fleetPrompt.data?.updatedAt ?? 'unset'}
            prompt={fleetPrompt.data ?? null}
          />
        )}
      </Panel>

      <Panel>
        <PanelHead title="Repos" meta={`${rows.length}`} />

        {repos.isPending && !rows.length ? (
          <p className="text-fg-3 px-gutter py-8 text-center text-sm">Loading the registry…</p>
        ) : rows.length === 0 ? (
          <div className="px-gutter py-12 text-center">
            <FolderGit2 className="text-fg-3 mx-auto mb-3 size-10" strokeWidth={1.5} />
            <p className="text-foreground text-lg font-medium">No repos registered</p>
            <p className="text-fg-3 mx-auto mt-1.5 max-w-md text-sm">
              A job's repos field has nothing to pick from until one is registered here. Existing
              jobs that already named a repo free text keep working either way.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Repo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Default branch</TableHead>
                <TableHead>Gates</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead className="text-right">Registered</TableHead>
                <TableHead className="sr-only">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((repo) => {
                return (
                  <Fragment key={repo.slug}>
                    <TableRow>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-2">
                          <RepoChip repo={repo.slug} />
                          {repo.githubUrl ? (
                            <a
                              href={repo.githubUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-fg-3 hover:text-foreground shrink-0 transition-colors duration-100"
                              aria-label={`Open ${repo.slug} on GitHub`}
                            >
                              <ExternalLink className="size-3.5" aria-hidden />
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={repo.enabled ? 'success' : 'secondary'}>
                          {repo.enabled ? 'enabled' : 'disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-fg-2 text-xs">
                        {repo.defaultBranch ?? <span className="text-fg-3">unknown</span>}
                      </TableCell>
                      <TableCell>
                        <GatesCell repo={repo} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {repo.instructions || repo.systemPrompt ? (
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary">set</Badge>
                            {repo.announceOnReady ? (
                              <Badge variant="success">announces</Badge>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-fg-3">fleet only</span>
                        )}
                      </TableCell>
                      <TableCell className="text-fg-2 truncate text-xs">{repo.addedBy}</TableCell>
                      <TableCell className="tnum text-fg-3 text-right text-2xs whitespace-nowrap">
                        {ago(minutesSince(repo.createdAt))}
                      </TableCell>
                      <TableCell className="text-right">
                        <RepoActions
                          repo={repo}
                          editing={editing === repo.slug}
                          onEditPrompt={() =>
                            setEditing((open) => (open === repo.slug ? null : repo.slug))
                          }
                          onDisable={() => {
                            disable.reset();
                            ask.open('disable-repo', repo.slug);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    {editing === repo.slug ? (
                      <TableRow>
                        <TableCell className="p-0" colSpan={8}>
                          <RepoPromptEditor
                            key={`${repo.slug}:${repo.instructions ?? ''}:${repo.systemPrompt ?? ''}`}
                            repo={repo}
                            onClose={() => setEditing(null)}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {ask.isOpen('disable-repo', repo.slug) && ask.ask ? (
                      <TableRow>
                        <TableCell className="p-0" colSpan={8}>
                          <AskBox
                            ask={ask.ask}
                            busy={disable.busy}
                            error={disable.error}
                            onCancel={ask.close}
                            onConfirm={() => disable.run(repo.slug)}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>
    </AdminShell>
  );
}
